import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../../../hooks/useAuth';
import { useFirebase } from '../../../hooks/useFirebase';
import { queryDelete, queryGet, queryPost, queryPut } from '../../tools/tools';

const ENDPOINT = '/consultants';
const KEY = 'consultants';

export const useConsultantEntityQuery = () => {
  const { user } = useAuth();
  const { remoteConfig } = useFirebase();
  const queryClient = useQueryClient();

  const [isMutating, setIsMutating] = useState(false);
  const [mutationError, setMutationError] = useState(null);

  const queryKey = [KEY, user?.uid];

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey,
    queryFn: () => queryGet(remoteConfig, ENDPOINT, user?.accessToken),
    enabled: !!user?.accessToken && !!remoteConfig
  });

  const wrapMutate = (mutationFn) => ({ data: payload, onSuccess, onError, onComplete }) => {
    setIsMutating(true);
    setMutationError(null);
    mutationFn(payload, {
      onSuccess: async (res) => {
        onSuccess?.(res);
        onComplete?.(null, res);
        await refetch();
      },
      onError: (err) => {
        setMutationError(err);
        onError?.(err);
        onComplete?.(err, null);
      },
      onSettled: () => setIsMutating(false)
    });
  };

  const { mutate: _create, isPending: isCreating, reset: resetCreate } = useMutation({
    mutationFn: (item) => queryPost(remoteConfig, ENDPOINT, item, user?.accessToken)
  });

  const { mutate: _update, isPending: isUpdating, reset: resetUpdate } = useMutation({
    mutationFn: (item) => queryPut(remoteConfig, ENDPOINT, item, user?.accessToken)
  });

  const { mutate: _remove, isPending: isDeleting, reset: resetRemove } = useMutation({
    mutationFn: (id) => queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken)
  });

  const { mutate: _deleteWithOptions, isPending: isDeletingCustom } = useMutation({
    mutationFn: ({ id, removeCoupons }) =>
      queryDelete(remoteConfig, ENDPOINT, id, user?.accessToken, { removeCoupons })
  });

  const { mutate: _resend } = useMutation({
    mutationFn: (id) => queryPost(remoteConfig, `${ENDPOINT}/${id}/resend-welcome`, {}, user?.accessToken)
  });

  const reset = () => { resetCreate(); resetUpdate(); resetRemove(); };

  const getById = async (id, onSuccess) => {
    try {
      setIsMutating(true);
      const result = await queryGet(remoteConfig, `${ENDPOINT}/${id}`, user?.accessToken);
      queryClient.setQueryData(queryKey, (old) => {
        if (!old) return [result];
        const idx = old.findIndex((e) => e.id === result.id);
        if (idx >= 0) { const u = [...old]; u[idx] = result; return u; }
        return [...old, result];
      });
      onSuccess?.(result);
      return result;
    } catch (err) {
      setMutationError(err);
      throw err;
    } finally {
      setIsMutating(false);
    }
  };

  const deleteConsultant = ({ id, removeCoupons, onSuccess, onError }) => {
    setIsMutating(true);
    setMutationError(null);
    _deleteWithOptions({ id, removeCoupons }, {
      onSuccess: async (res) => {
        onSuccess?.(res);
        await refetch();
      },
      onError: (err) => {
        setMutationError(err);
        onError?.(err);
      },
      onSettled: () => setIsMutating(false)
    });
  };

  const resendWelcome = (id) => _resend(id);

  return {
    data: data ?? [],
    loading: isLoading,
    error: isError,
    create: wrapMutate(_create),
    update: wrapMutate(_update),
    remove: wrapMutate(_remove),
    getById,
    reset,
    refetch,
    mutating: isCreating || isUpdating || isDeleting || isDeletingCustom || isMutating,
    mutatingError: mutationError,
    // panel-specific extras (not used by EntityTable)
    deleteConsultant,
    resendWelcome
  };
};
